/**
 * Top-level gantt adapter — orchestrates snapshot + engine analysis.
 *
 * Implements `05-` §C.6 (Phase B-light variant). Phase B exposes only the
 * analysis entry (`analyzeViaEngine`); the planner entry (`autoSchedule`
 * with two-phase D-01 flow) is Phase C.
 *
 * Per `05-` §G.3, snapshot construction is gated by `RevisionTracker` so
 * repeat calls within the same gantt-state revision return the same
 * Snapshot reference — engine memoization (memoize1 on `analyze`) then
 * yields the same AnalysisResult reference. The "render 50 critical-path
 * markers in one frame" case becomes a single graph build + analysis pass.
 */

import { analyze, getSlackBetween } from "../core/api";
import { buildSnapshot, type GanttSnapshotBackend } from "./snapshot_builder";
import { RevisionTracker } from "./revision_tracker";
import { getOverridesFor, type GanttAnchorBackend } from "./anchor_cache";
import type { AnalysisResult, Snapshot, TaskID, NormalizedTaskID, AnchorOverride } from "../core/types";
import { normalizeTaskId, taskIdLookupCandidates } from "../core/ids";

/**
 * Per-gantt-instance adapter state. Held on the gantt instance via a
 * symbol-keyed property (Phase B uses a plain WeakMap to avoid mutating
 * the gantt prototype).
 */
interface AdapterState {
  tracker: RevisionTracker;
  snapshotRev: number;
  snapshot: Snapshot | null;
}

const stateMap = new WeakMap<object, AdapterState>();

function getState(gantt: GanttSnapshotBackend): AdapterState {
  let s = stateMap.get(gantt as unknown as object);
  if (!s) {
    s = { tracker: new RevisionTracker(), snapshotRev: -1, snapshot: null };
    stateMap.set(gantt as unknown as object, s);
  }
  return s;
}

/**
 * Construct (or reuse) the current Snapshot for a gantt instance.
 *
 * Reuse rule: if the tracker's revision hasn't changed since the last
 * snapshot, return the cached Snapshot. Otherwise rebuild and cache.
 *
 * Phase B exposes this so test fixtures and adapter consumers can read the
 * snapshot directly. Phase C wires the tracker to gantt events; Phase B
 * leaves it inert (tracker never bumps without explicit calls), which means
 * the first `analyzeViaEngine` call builds the snapshot and every
 * subsequent call within the same test reuses it. That's adequate for
 * Phase B; cache invalidation correctness is covered by RevisionTracker
 * unit tests already.
 */
export function getOrBuildSnapshot(gantt: GanttSnapshotBackend): Snapshot {
  const s = getState(gantt);
  if (s.snapshot && s.tracker.current === s.snapshotRev) {
    return s.snapshot;
  }
  // Per D-19: consult the anchor cache for pre-mutation bboxes captured by
  // `auto_schedule_triggers.ts` (drag / lightbox / inline-editor windows).
  // Without this the engine sees the post-mutation live bbox for the
  // dragged summary AND its non-dragged siblings, which makes summary-drag
  // under `move_projects: true` lose the pre-mutation anchor that D-08
  // translation depends on. The cache returns an empty Map when no
  // capture window is open, so the no-drag path remains snapshot-from-live.
  const anchorOverrides = filterAnchorOverrides(
    gantt as unknown as GanttAnchorBackend,
    getOverridesFor(gantt as unknown as GanttAnchorBackend)
  );
  s.snapshot = buildSnapshot(gantt, { anchorOverrides });
  s.snapshotRev = s.tracker.current;
  return s.snapshot;
}

/**
 * Filter the raw bbox capture per D-19: include an override only when the
 * captured bbox actually differs from gantt's current live bbox for that
 * task AND the task isn't the drag target itself (the drag target's live
 * bbox IS its current state — including it as an override would just feed
 * the engine its anchor twice).
 *
 * The cache returns the full pre-mutation capture (see
 * `anchor_cache.getOverridesFor`); this filter is the boundary that
 * decides "which captures are actual overrides for engine purposes."
 */
function filterAnchorOverrides(
  gantt: GanttAnchorBackend,
  raw: ReadonlyMap<TaskID, AnchorOverride>
): Map<NormalizedTaskID, AnchorOverride> {
  const result = new Map<NormalizedTaskID, AnchorOverride>();
  if (raw.size === 0) return result;
  const draggedId = readDraggedTaskId(gantt);
  for (const [id, captured] of raw) {
    const normalizedId = normalizeTaskId(id);
    if (draggedId !== undefined && normalizedId === draggedId) continue;
    let live: { start_date?: Date; end_date?: Date } | undefined;
    try {
      const reader = (gantt as unknown as { getTask?(id: TaskID): { start_date?: Date; end_date?: Date } });
      for (const candidate of taskIdLookupCandidates(id)) {
        live = reader.getTask?.(candidate);
        if (live?.start_date && live.end_date) break;
      }
    } catch { /* task may not exist anymore — skip */ }
    if (!live || !live.start_date || !live.end_date) continue;
    if (
      live.start_date.getTime() !== captured.start.getTime() ||
      live.end_date.getTime() !== captured.end.getTime()
    ) {
      result.set(normalizedId, captured);
    }
  }
  return result;
}

function readDraggedTaskId(gantt: GanttAnchorBackend): NormalizedTaskID | undefined {
  try {
    const state = (gantt as unknown as { getState?(): { tasksDnd?: { drag_id?: TaskID } } }).getState?.();
    const id = state?.tasksDnd?.drag_id;
    return id === undefined || id === null ? undefined : normalizeTaskId(id);
  } catch {
    return undefined;
  }
}

/**
 * Run the engine's `analyze()` against the current gantt state.
 *
 * @returns AnalysisResult with totalSlack/freeSlack/isCritical (in minutes,
 *          per D-35) plus connectedGroups and cycles.
 *
 * Callers (e.g., the rewired `gantt.getTotalSlack`) convert the minute
 * values to the gantt-configured duration unit at the public-method
 * boundary.
 */
export function analyzeViaEngine(gantt: GanttSnapshotBackend): AnalysisResult {
  const snapshot = getOrBuildSnapshot(gantt);
  return analyze(snapshot);
}

/**
 * Engine-backed `getSlack(t1, t2)` — returns slack in minutes (engine
 * standard unit per D-35). Caller (the rewired `gantt.getSlack`) converts
 * to the configured duration unit at the public-method boundary. Returns
 * `Infinity` when no simple path connects t1 and t2; caller decides how
 * to surface that (Phase B preserves v1's date-arithmetic fallback).
 */
export function getSlackBetweenViaEngine(
  gantt: GanttSnapshotBackend,
  t1: TaskID,
  t2: TaskID
): number {
  const snapshot = getOrBuildSnapshot(gantt);
  return getSlackBetween(snapshot, t1, t2);
}

/**
 * Engine-backed `autoSchedule(id?, inclusive?)` — re-exported here so the
 * gantt-side wiring (Phase C Day 13 `ext/auto_scheduling.js`) has a single
 * import surface. The implementation lives in `event_bridge.ts`; see that
 * module for the D-01 two-phase plan flow + D-17 event order + D-18
 * per-variant arg mapping.
 */
export { autoScheduleViaEngine, type AutoScheduleResult, type GanttEventBridgeBackend } from "./event_bridge";

/**
 * Phase C Day 11 — attach v2 UI-trigger handlers (drag, link change, parse,
 * lightbox save, inline edit, batchUpdate) so they route auto-schedule
 * through `autoScheduleViaEngine` instead of v1's `gantt.autoSchedule`.
 * Idempotent; honors `gantt._autoscheduling_in_progress` re-entrance guard.
 *
 * Day 13 wires this into `ext/auto_scheduling.js` under `_engine === "v2"`.
 */
export { attachAutoScheduleTriggers, type GanttTriggersBackend } from "./auto_schedule_triggers";

/**
 * Force a snapshot rebuild on the next `analyzeViaEngine` call. Phase C
 * uses this from its gantt-event wiring; Phase B tests can call it
 * directly to simulate mutations.
 */
export function bumpRevision(gantt: GanttSnapshotBackend): void {
  getState(gantt).tracker.bump();
}

/**
 * Expose the tracker for the wiring layer (Phase C wires gantt events
 * to `tracker.bump`).
 */
export function getRevisionTracker(gantt: GanttSnapshotBackend): RevisionTracker {
  return getState(gantt).tracker;
}

/**
 * Phase C Day 12 — full D-20 snapshot-invalidation wiring.
 *
 * Wires the `RevisionTracker` to gantt-side mutations so the snapshot cache
 * invalidates only when scheduling-relevant data changes. Four layers:
 *
 *   1. Task add/update/delete + link add/update/delete + parse/clear/
 *      taskIdChange + batchUpdate (already in place from Phase B).
 *   2. **Task-update diff classifier** (NEW Day 12). The `onAfterTaskUpdate`
 *      handler used to bump unconditionally; now it diffs against the
 *      pre-update snapshot (cached in `onBeforeTaskUpdate`) and consults
 *      `tracker.classifyTaskUpdate` so a cosmetic text/color edit does NOT
 *      invalidate the cache.
 *   3. **Calendar / group API wrappers** (NEW Day 12). Gantt fires no events
 *      for `addCalendar` / `setWorkTime` / `unsetWorkTime` / `deleteCalendar`
 *      / `groupBy`. We monkey-patch each method to bump after invocation.
 *      Methods are tagged `__v2_wrapped` so a second `wireRevisionTracker`
 *      call doesn't re-wrap.
 *   4. **Config-write `defineProperty` setters** (NEW Day 12). Gantt has no
 *      setter hook for `gantt.config.X = Y` — plain JS assignment. We install
 *      `Object.defineProperty(gantt.config, key, { get, set })` for each
 *      scheduling-relevant top-level key so the setter bumps. Limitation:
 *      nested writes (e.g., `gantt.config.auto_scheduling.enabled = true`)
 *      do NOT fire the outer setter. Programmatic nested writers must call
 *      `bumpRevision(gantt)` OR use replacement-style writes
 *      (`gantt.config.auto_scheduling = { ...gantt.config.auto_scheduling, ... }`).
 *
 * Idempotent — safe to call multiple times on the same gantt instance.
 */
export function wireRevisionTracker(gantt: GanttEventBackend): void {
  const s = getState(gantt as unknown as GanttSnapshotBackend);
  if ((s as { _wired?: boolean })._wired) return;
  (s as { _wired?: boolean })._wired = true;

  const tracker = s.tracker;
  const bump = () => tracker.bump();

  // ── Layer 1: pre-existing event wiring ────────────────────────────────
  gantt.attachEvent?.("onAfterTaskAdd", bump);
  gantt.attachEvent?.("onAfterTaskDelete", bump);
  gantt.attachEvent?.("onAfterLinkAdd", bump);
  gantt.attachEvent?.("onAfterLinkUpdate", bump);
  gantt.attachEvent?.("onAfterLinkDelete", bump);
  gantt.attachEvent?.("onParse", bump);
  gantt.attachEvent?.("onClear", bump);
  // `onTaskIdChange` fires from inside `gantt.changeTaskId` BEFORE that
  // facade walks the renamed task's `$source`/`$target` link arrays and
  // updates each `link.source` / `link.target` field to point at `newId`.
  // Other listeners on the same event — notably `ext/critical_path/critical_path`'s
  // `refreshTask(newId)` handler — render templates that call
  // `gantt.isCriticalTask`, which routes through v2 analysis and rebuilds
  // the snapshot. If we only bump once at event-fire time, that rebuild
  // captures STALE links (still pointing at the old id) and gets cached.
  // Subsequent `isCriticalTask` calls then return wrong results because
  // the renamed task has no outgoing edge in the cached analysis graph.
  //
  // Fix: bump now (for any listener that rebuilds during the event) AND
  // schedule a deferred bump + critical-path refresh after the current
  // event chain — by then `changeTaskId` has finished updating the links,
  // so the next snapshot rebuild sees the fully-consistent state. The
  // deferred `refreshTask` repaints the task row so the
  // `.gantt_critical_task` class reflects the corrected analysis.
  // retest_migrations/critical_path test 2 ("keeps critical-path
  // calculation valid after changing a task id") passes because mocha
  // doesn't run the `refreshTask` template round-trip; the puppeteer-driven
  // `10_critical_path/005_task_id_change` does, and exercises this bug.
  gantt.attachEvent?.("onTaskIdChange", (_oldId: unknown, newId: unknown) => {
    bump();
    // Defer to after the synchronous tail of `gantt.changeTaskId` (which
    // updates link.source/target post-event). Promise.resolve().then runs
    // in the microtask queue — same JS turn, after current sync work
    // completes.
    Promise.resolve().then(() => {
      bump();
      const g = gantt as unknown as {
        config?: { highlight_critical_path?: boolean };
        isTaskExists?: (id: unknown) => boolean;
        refreshTask?: (id: unknown) => void;
      };
      if (g.config?.highlight_critical_path && g.isTaskExists?.(newId) && g.refreshTask) {
        g.refreshTask(newId);
      }
    });
    return true;
  });
  gantt.attachEvent?.("onBeforeBatchUpdate", () => tracker.beginBatch());
  gantt.attachEvent?.("onAfterBatchUpdate", () => tracker.endBatch());

  // ── Layer 2: task-update invalidation ────────────────────────────────
  //
  // Always bump on task update — the previous diff classifier (a perf
  // optimization meant to skip cosmetic-only updates) didn't work in
  // practice: gantt fires both `onBeforeTaskUpdate` and `onAfterTaskUpdate`
  // with the SAME live task reference, and idiomatic user code mutates
  // `task.start_date` BEFORE calling `gantt.updateTask(id)`. By the time
  // onBeforeTaskUpdate fires, the task already carries the new values, so
  // pickRelevant cached the post-mutation state as "old"; the subsequent
  // diff against the identical "new" object always returned no changes.
  // Net effect was zero invalidation on every programmatic update — see
  // GS-1689 test scenarios.
  //
  // Always-bump is correct and simple. The cost is rebuilding the snapshot
  // on cosmetic updates (text color, etc.), which is bounded by task count
  // and negligible at typical scales. If perf later demands a real diff
  // classifier, it needs its own shadow state cache that the engine
  // maintains independently of gantt's event lifecycle.
  gantt.attachEvent?.("onAfterTaskUpdate", () => {
    tracker.bump();
    return true;
  });

  // ── Layer 3: calendar / group API monkey-patches ──────────────────────
  for (const method of ["addCalendar", "deleteCalendar", "setWorkTime", "unsetWorkTime", "groupBy"] as const) {
    wrapMethodBumpAfter(gantt as unknown as Record<string, unknown>, method, tracker);
  }

  // ── Layer 4: config-write defineProperty setters ──────────────────────
  installConfigSetters(gantt as unknown as GanttConfigBackend, tracker);
}

/**
 * Top-level `gantt.config.*` keys that bump the snapshot cache on write.
 * The list mirrors `SCHEDULING_RELEVANT_CONFIG_KEYS` in `revision_tracker.ts`
 * minus `types`/`links` (those rarely mutate at runtime and re-creating
 * the setter for them risks breaking gantt's own internal property-style
 * config initialization).
 *
 * Nested writes (`gantt.config.auto_scheduling.enabled = true`) do NOT
 * fire — see the file header note.
 */
const CONFIG_KEYS_TO_OBSERVE: ReadonlyArray<string> = [
  "auto_scheduling",
  "auto_scheduling_compatibility",
  "auto_scheduling_strict",
  "auto_scheduling_move_asap_tasks",
  "auto_scheduling_descendant_links",
  "auto_scheduling_initial",
  "auto_scheduling_move_projects",
  "auto_scheduling_use_progress",
  "auto_scheduling_project_constraint",
  "schedule_from_end",
  "inherit_calendar",
  "work_time",
  "correct_work_time",
  "duration_unit",
  "duration_step",
  "project_start",
  "project_end"
];

function wrapMethodBumpAfter(
  gantt: Record<string, unknown>,
  method: string,
  tracker: RevisionTracker
): void {
  const orig = gantt[method];
  if (typeof orig !== "function") return;
  if ((orig as { __v2_wrapped?: boolean }).__v2_wrapped) return;
  const wrapped = function (this: unknown, ...args: unknown[]) {
    const result = (orig as (...a: unknown[]) => unknown).apply(this, args);
    tracker.bump();
    return result;
  };
  (wrapped as { __v2_wrapped?: boolean }).__v2_wrapped = true;
  gantt[method] = wrapped;
}

function installConfigSetters(gantt: GanttConfigBackend, tracker: RevisionTracker): void {
  const cfg = gantt.config;
  if (!cfg) return;
  for (const key of CONFIG_KEYS_TO_OBSERVE) {
    const existing = Object.getOwnPropertyDescriptor(cfg, key);
    if (existing?.set && (existing.set as { __v2_wrapped?: boolean }).__v2_wrapped) continue;
    const backing = { value: (cfg as unknown as Record<string, unknown>)[key] };
    try {
      Object.defineProperty(cfg, key, {
        configurable: true,
        enumerable: true,
        get(): unknown { return backing.value; },
        set(v: unknown): void {
          backing.value = v;
          tracker.bump();
        }
      });
      const desc = Object.getOwnPropertyDescriptor(cfg, key);
      if (desc?.set) (desc.set as { __v2_wrapped?: boolean }).__v2_wrapped = true;
    } catch {
      // Some config keys may be non-configurable (defined by gantt's own
      // initialization). Skip those — manual `bumpRevision(gantt)` is the
      // fallback for users programmatically mutating such keys.
    }
  }
}

/**
 * Structural subset for the event wiring — accepts gantt's
 * `attachEvent` surface plus the method/config surfaces Day 12 wraps.
 */
export interface GanttEventBackend {
  attachEvent?: (name: string, handler: (...args: unknown[]) => unknown) => unknown;
}

interface GanttConfigBackend {
  config?: { [key: string]: unknown };
  _getAutoSchedulingConfig?: () => { use_progress?: boolean; [k: string]: unknown };
}
